/*
 * PageWright — My Library (S6), vanilla JS, no framework.
 *
 * Renders the signed-in customer's OWNED downloads by calling the token-gated backend:
 *   GET {backend}/api/library   (Authorization: Bearer <library JWT>, aud=pw:library)
 *   → { items: Entitlement[] }
 *
 * Token seam (mirrors the SPA exactly — packages/web/src/api-client.ts):
 *   - An inbound `?token=<jwt>` is stashed in sessionStorage under `pw_library_token` and stripped
 *     from the address bar. The library call then reads it lazily from sessionStorage.
 *   - My Library Shopify-login bridge, Phase 2 (2026-07-14): no token AND Liquid rendered a real
 *     `customer.id` → try GET /apps/library (Shopify App Proxy, same-origin, rides the real
 *     session cookies) before giving up. Success mints/stores a token exactly like an emailed
 *     link would; any failure (not logged in, Shop Pay identity mismatch, app not installed yet)
 *     falls through to the UNCHANGED request-link form, never an error.
 *   - No token AND no logged-in customer.id → the request-link form renders and the library API
 *     is NEVER called (guests have no library).
 *   - A STORED token that 401/403's also retries the bridge once (guarded by bridgeRetried).
 *   - tasks.db #815 (2026-09-01): a stored token that comes back 200 with items:[] ALSO retries
 *     the bridge once (same bridgeRetried guard) when Liquid rendered a real customer.id — a
 *     cryptographically valid token can still belong to a DIFFERENT identity than whoever is
 *     really logged into Shopify right now (Shop Pay silently authenticates as its own saved
 *     identity), which is a 200-empty response, not a 401 — the 401/403 case above never sees it.
 *     If the bridge also comes back empty, falls through to the ordinary empty-library UI exactly
 *     as before. 2026-09-01 3-model audit follow-up: bridgeRetried is now set at ALL FOUR places a
 *     bridge-obtained token can reach loadLibrary() (not just this file's own two retry branches),
 *     so a genuinely-empty customer — stored token or not — never pays for more than one bridge
 *     round trip per page load; and the retry no longer blanks the screen (the existing "Loading
 *     your library…" status stays up for its duration instead of being cleared first).
 *
 * Downloads:
 *   - Every item (page AND ebook) downloads via `ent.downloadUrl` — a signed, short-lived
 *     `/api/download?token=...` link the backend mints per item (same signer the initial
 *     delivery email uses). NEVER link to `ent.ebookUrl` directly — that's the raw stored value
 *     on the original (now-private, post-P2.5) bucket domain; it 401s.
 *
 * Request-a-link form (P1, emailed identity):
 *   POST {backend}/api/library-link  { email }  ->  200 (always, whether or not the email has
 *   purchases — the endpoint is deliberately non-revealing so this UI must never say which),
 *   429 (rate-limited), or any other status (generic error). Shown whenever there's no usable
 *   token: none stored/in the URL to begin with, or the token turned out to be expired/invalid
 *   (the /api/library call itself 401/403'd).
 */
(function () {
  'use strict';

  /* Same literal PWAuth.TOKEN_KEY holds -- referenced directly so there's one real source of
   * truth once PWAuth has loaded (see layout/theme.liquid load order), literal kept as the
   * pre-load/failed-load fallback. */
  var TOKEN_KEY = (window.PWAuth && window.PWAuth.TOKEN_KEY) || 'pw_library_token';
  // Freebie (zero-priced) lines are minted with an effectively-unlimited budget server-side --
  // source of truth: ENTITLEMENT_RULES.freebieMaxDownloads in @pagewright/contracts. Duplicated
  // here because theme assets have no shared import boundary with the backend package.
  var FREEBIE_MAX_DOWNLOADS = 999;

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function setStatus(statusEl, message) {
    if (!statusEl) return;
    if (message) {
      statusEl.textContent = message;
      statusEl.hidden = false;
    } else {
      statusEl.textContent = '';
      statusEl.hidden = true;
    }
  }

  /**
   * Token persistence (2026-08-02). Paul: "if you are in your library then navigate away and try
   * to come back you are forced to login again."
   *
   * The token lived in sessionStorage only — scoped to ONE tab and ONE origin, so it was thrown
   * away by closing the tab, opening a link in a new tab, or moving between the myshopify preview
   * host and www. All three happen constantly while testing, and the last one happens to real
   * customers who arrive from an emailed link.
   *
   * That contradicted the token's own design: it is a 14-DAY signed link. A credential deliberately
   * issued for a fortnight should not evaporate when a tab closes. The original comment gives no
   * security reason — it says the seam "mirrors the SPA exactly", i.e. parity, not intent.
   *
   * Trade-off, stated: the token now outlives the tab on a shared device. It is a downloads
   * library rather than anything payment-bearing, the same 14-day token already travels by email,
   * and Favourites already persists in localStorage — so this matches the surface's existing
   * posture rather than widening it.
   */
  // 2026-08-13/14: delegates to the shared window.PWAuth (assets/pw-auth.js, loaded before this
  // file — see layout/theme.liquid) now that pw-favorites.js and pw-in-cart.js need the exact
  // same read/write/clear semantics this function originated. Body kept as a fallback so a
  // failed asset load still degrades to this file's own (already-correct) behavior.
  function storeToken(token) {
    if (window.PWAuth) return window.PWAuth.storeToken(token);
    if (!token) return;
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch (e) {
      /* storage full/blocked — fall through to sessionStorage below */
    }
    try {
      sessionStorage.setItem(TOKEN_KEY, token);
    } catch (e) {
      /* storage full/blocked — token just won't persist */
    }
  }

  /**
   * Clear BOTH stores. This is the dangerous half of adding a second store: the 401 path used to
   * remove the token from sessionStorage alone, so leaving that untouched would let a revoked or
   * expired token resurrect from localStorage on the very next load — a sign-out that never sticks.
   * One helper, so no call site can drift.
   */
  function clearToken() {
    if (window.PWAuth) return window.PWAuth.clearToken();
    try {
      sessionStorage.removeItem(TOKEN_KEY);
    } catch (e) {
      /* blocked storage must not throw during sign-out */
    }
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch (e) {
      /* blocked storage must not throw during sign-out */
    }
  }

  /*
   * BUG-49 (security, 2026-08-19) — the emailed My Library link now carries its token in the URL
   * FRAGMENT (`#token=...`), not a query param: a fragment is never sent to any server (not in
   * the request line, not in a Referer header), closing a leak a query-string token exposed
   * (server/CDN access logs, browser history, third-party resources loaded before the strip).
   * See pagewright PR #277 for the mint side.
   *
   * The fragment is checked FIRST (the current, secure link shape). Falls back to the OLD
   * `?token=` query param so already-sent emails (up to the 14-day link TTL) still work — do
   * NOT drop this fallback, it would strand every customer with an unread email in their inbox
   * from before this shipped. Whichever is found is stripped from the visible URL, same as
   * before.
   */
  /*
   * tasks.db #816 (2026-09-01, PLAN v3 §2.4 point 1) -- persistence is NO LONGER this function's
   * job. It used to call storeToken(token) unconditionally the instant a URL token was found,
   * which is exactly what let a hostile link (carrying the ATTACKER's own valid token) durably
   * plant a foreign credential sitewide before anything ever checked it — and pw-favorites.js's
   * mergeOnLogin() would then merge the victim's device favorites into the attacker's account the
   * next time any page ran with a token present. This function now only reads + strips the URL
   * (unchanged: BUG-49's fragment-first, `?token=` fallback, security-motivated stripping order)
   * and returns the token; the CALLER (currentTokenInfo()/init()) decides whether/when to persist
   * it, based on the identity-crosscheck mode and gate below. Structural test (f) pins that this
   * function contains no storeToken call at all, in any form.
   */
  // Memoized (2026-09-01, #816 fix-during-implementation): reading the URL is now DESTRUCTIVE in
  // a new way once storeToken() moved out of this function -- the very first read strips and
  // discards the token from the address bar whether or not anything ends up persisting it, so a
  // SECOND call (there are two: setActiveTab(initialTab()) -> renderLibraryTab() ->
  // updateZipBar() -> currentToken() runs BEFORE init()'s own gating check further down) would
  // see an already-stripped URL and silently report "no token" even though a real one existed.
  // Previously this was masked by accident: the old unconditional storeToken() on the FIRST call
  // persisted the token immediately, so the second call's fallback to storage still found it. Now
  // that persistence is a separate, gated decision, the URL itself must be parsed exactly ONCE per
  // page load; every call after the first returns the SAME cached answer.
  var locationTokenChecked = false;
  var locationTokenCached;

  function readTokenFromLocation() {
    if (locationTokenChecked) return locationTokenCached;
    locationTokenChecked = true;
    if (typeof window === 'undefined' || !window.location) {
      locationTokenCached = undefined;
      return undefined;
    }
    var token;
    try {
      var url = new URL(window.location.href);
      var hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
      token = hashParams.get('token') || url.searchParams.get('token') || undefined;
      if (token) {
        hashParams.delete('token');
        url.hash = hashParams.toString();
        url.searchParams.delete('token');
        if (window.history && window.history.replaceState) {
          window.history.replaceState(null, '', url.toString());
        }
      }
    } catch (e) {
      /* malformed URL — ignore */
    }
    locationTokenCached = token;
    return token;
  }

  /*
   * `{ token, fromLocation }` — the ONE thing every caller needs to make an identity-crosscheck
   * decision (Audit-1 SF-7: this is a real API change, not "thread a flag" through the old
   * currentToken()). `currentToken()` itself remains a thin accessor over the module's ACTIVE
   * token (see `activeToken` below) so its other call site (updateZipBar/buildDownloadPlan)
   * keeps working, including for an interstitial-confirmed view-only token that never touched
   * storage.
   */
  function currentTokenInfo() {
    var t = readTokenFromLocation();
    if (t) return { token: t, fromLocation: true };
    if (window.PWAuth) return { token: window.PWAuth.getToken(), fromLocation: false };
    try {
      return {
        token: sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY) || undefined,
        fromLocation: false,
      };
    } catch (e) {
      return { token: undefined, fromLocation: false };
    }
  }

  /* Module-level, set by loadLibrary() (§2.4 point 2). Single `pw-library` instance per page in
   * real usage — initAll() looping multiple instances would share this var, an accepted,
   * documented single-instance assumption, not a code guard. */
  var activeToken;

  function currentToken() {
    if (activeToken) return activeToken;
    return currentTokenInfo().token;
  }

  /*
   * §2.4.5 — split on the FIRST `@`; local part -> first char + bullets; domain -> split on `.`,
   * mask only the first label, join the rest unchanged (handles multi-part suffixes like
   * `co.uk`, where "mask everything but the TLD" is undefined). No `@`, leading/trailing `@`, or
   * empty input -> a fixed placeholder, NEVER throws. Pure and fully synchronous — takes an
   * ALREADY-DECODED string (tokenClaims()' UTF-8-safe output, §2.3) and does no decoding of its
   * own.
   */
  function maskEmail(email) {
    if (typeof email !== 'string' || !email) return '•••';
    var at = email.indexOf('@');
    if (at <= 0 || at === email.length - 1) return '•••';
    var local = email.slice(0, at);
    var domain = email.slice(at + 1);
    var labels = domain.split('.');
    if (!labels[0]) return '•••';
    var masked = local.charAt(0) + '•••' + '@' + labels[0].charAt(0) + '•••';
    if (labels.length > 1) {
      masked += '.' + labels.slice(1).join('.');
    }
    return masked;
  }

  /*
   * FIX-UP (2026-09-01, Sonnet #4 -- LOW): this file used to keep its OWN local function reading
   * window.PW_IDENTITY_CROSSCHECK, a verbatim-identical copy of assets/pw-auth.js's
   * identityCrosscheckMode() -- exactly the class of drift risk the rest of this feature was
   * careful to avoid elsewhere (the shared decoder, the shared identityCheck). Removed in favor of
   * calling window.PWAuth.identityCrosscheckMode() directly at the one call site below, matching
   * how this file already reuses window.PWAuth.identityCheck/tokenClaims/postIdentityCheckEvent
   * rather than a second implementation of any of those. Fails CLOSED to 'off' if PWAuth somehow
   * isn't loaded — never assume 'enforce'.
   */

  /* window.PWAuth.identityCheck/tokenClaims are the shared decision function (assets/pw-auth.js
   * §2.2) — never a second, ad-hoc reimplementation here. Fails to the value that NEVER acts on
   * anything (indeterminate_undecodable) if PWAuth somehow isn't loaded. */
  function crosscheckDecision(token, sessionCustomerId, sessionEmail) {
    try {
      if (!window.PWAuth || !window.PWAuth.identityCheck) {
        return { decision: 'indeterminate_undecodable', claims: undefined };
      }
      var claims = window.PWAuth.tokenClaims ? window.PWAuth.tokenClaims(token) : undefined;
      return { decision: window.PWAuth.identityCheck(claims, sessionCustomerId, sessionEmail), claims: claims };
    } catch (e) {
      return { decision: 'indeterminate_undecodable', claims: undefined };
    }
  }

  function postIdentityCheckEvent(decision, layer, mode) {
    if (window.PWAuth && window.PWAuth.postIdentityCheckEvent) {
      window.PWAuth.postIdentityCheckEvent(decision, layer, mode);
    }
  }

  // Ported from bap/src/lib/title-hygiene.js (isJunkTitle/sanitizeTitle) -- that file exists
  // because Shopify webhook payloads sometimes deliver a product_type string or a numeric
  // product_id as the line item title. pw-library.js has the same exposure one level further
  // down the fallback chain: ent.title missing/junk falls through to a raw handle/slug, the
  // exact class of bug, just reached a different way.
  // 'collage art page' added 2026-07-14, real production bug: every individual-page purchase
  // shares ONE generic Shopify product ("Collage Art Page", the custom-page-order variant used
  // across every book), so Shopify's line_item.title is this literal generic string regardless
  // of which book the page came from -- exactly the "junk title masking the real book" exposure
  // this whole function exists to catch, just via a shared-product name instead of a category
  // word. 21 real cs_downloads rows had this exact stored title when found.
  var JUNK_WORDS = {
    'digital': 1, 'digital download': 1, 'ebook': 1, 'e-book': 1, 'ebooks': 1,
    'collage art page': 1,
  };
  function isJunkTitle(t) {
    if (t == null) return true;
    var s = String(t).trim();
    if (!s) return true;
    if (/^\d+$/.test(s)) return true;
    if (JUNK_WORDS[s.toLowerCase()]) return true;
    // URL slug / product handle pattern: only lowercase letters, digits, and hyphens.
    // Real titles have spaces, capitals, punctuation.
    if (/^[a-z0-9][a-z0-9-]+[a-z0-9]$/.test(s)) return true;
    return false;
  }

  /* Humanize a handle for a readable fallback title: `spring-blossoms-ebook` -> `Spring Blossoms Ebook`. */
  function humanizeHandle(handle) {
    return String(handle || '')
      .split(/[-_/]+/)
      .filter(function (w) { return w.length > 0; })
      .map(function (w) { return w.charAt(0).toUpperCase() + w.slice(1); })
      .join(' ');
  }

  /*
   * Real title when it's not junk; otherwise the INDEXED catalog title; otherwise a humanized
   * handle -- never the raw slug.
   *
   * `indexedTitle` (2026-09-20) is the catalog title the backend joins from cs_search_docs on the
   * same read that produces thumbUrl. It matters because `cs_downloads.title` is whatever the
   * Shopify line item happened to carry at mint time and is NULL on the large majority of rows
   * (295 of Melissa Dilworth's 351, for one real library), which until now meant a humanized slug
   * -- "Unified Front Collage" -- was the best name a customer ever saw. It is junk-checked with
   * the same isJunkTitle() as the stored title: an index title can be a product_type string or a
   * slug exactly as easily as a line-item title can, and this fallback must not smuggle past the
   * guardrail test_library_junk_titles_070708.py exists to hold.
   *
   * Null/absent for a delisted product (its doc is pruned out of the ACTIVE-only index), which is
   * precisely the case isDelisted() below then labels rather than leaving it looking broken.
   */
  function resolveTitle(ent) {
    if (!isJunkTitle(ent.title)) return ent.title;
    if (!isJunkTitle(ent.indexedTitle)) return ent.indexedTitle;
    var handle = ent.productHandle || ent.bookHandle;
    return handle ? humanizeHandle(handle) : 'Untitled';
  }

  /*
   * Has the owning product left the live catalog (archived / draft / unlisted)?
   *
   * Paul, 2026-09-20, verbatim: "we need ALL customers to SEE their ARCHIVED products - just
   * because they are not on the store anymore does NOT take the entitlements away." So this is
   * DISPLAY-ONLY and must never gate anything: the item is rendered and its Download link is
   * built exactly as for any other item. It exists because a delisted product loses BOTH
   * server-side display sources at the same instant -- cs_search_docs is pruned when a product
   * leaves ACTIVE, so thumbUrl and indexedTitle go null together -- and an artwork-less row
   * labelled with a slug reads as a broken entry rather than as something the customer owns.
   *
   * Strictly `=== false`. `undefined` means the read never consulted the index (an older API
   * build, a single-row read); guessing "no longer sold" from missing information would mislabel
   * live products the moment the index lags.
   */
  function isDelisted(ent) {
    return ent && ent.catalogListed === false;
  }

  /* The one place the delisted copy lives, so list view and grid view cannot drift apart. */
  function delistedNote() {
    return el('div', 'pw-library__item-retired', 'No longer sold · yours to keep');
  }

  /* Case-insensitive substring match against the SAME resolved title buildItem() displays,
   * so a search never appears to miss an item the list is actually showing. */
  function itemMatchesQuery(ent, query) {
    if (!query) return true;
    return resolveTitle(ent).toLowerCase().indexOf(query.toLowerCase()) !== -1;
  }

  /* Favorites, Phase 3 (2026-07-21): humanize a favorited subject's title. WishlistItem carries
   * no title/thumbnail (owner-derived, subject-only per the wishlist contracts) — reuse the same
   * humanizeHandle fallback the Library tab already uses for junk/missing titles, rather than
   * adding a new metadata fetch this phase doesn't need. */
  function favoriteTitle(subject) {
    var handle = subject.kind === 'page' ? subject.productHandle : subject.handle;
    return humanizeHandle(handle);
  }

  function favoriteSubtitle(subject) {
    return subject.kind === 'page' ? 'Single Image · Page ' + subject.pageNumber : 'eBook';
  }

  function buildRemoveButton(item, onRemove) {
    var removeBtn = el('button', 'pw-library__fav-remove', 'Remove');
    removeBtn.type = 'button';
    removeBtn.setAttribute('data-pw-fav-remove', '');
    removeBtn.setAttribute('aria-label', 'Remove ' + favoriteTitle(item.subject) + ' from Favorites');
    removeBtn.addEventListener('click', function () {
      window.PWFavorites.removeFavorite(item.subject);
      onRemove();
    });
    return removeBtn;
  }

  /**
   * Add to Cart on a favourite (2026-08-02, Paul: "an Add to Cart button below if they are ready
   * to buy").
   *
   * PRODUCT-KIND ONLY, and that is a correctness boundary rather than a scoping shortcut: a PAGE
   * is priced by the cart's tier logic (assets/pw-page-cart.js recomputes t1/t2/t3 as pages are
   * added and removed), so a naive /cart/add.js of a page variant would add it at the wrong price
   * — the exact class of bug that mispriced two real orders in July. Page favourites keep their
   * deep link into the book, where the real selection flow lives. Asserted in both directions by
   * tests/test_library_favorites_ux_080202.py.
   *
   * Uses Shopify's own AJAX endpoints (no backend call): /products/<handle>.js for the variant,
   * then POST /cart/add.js. Every failure lands on the button as text — never a silent no-op and
   * never a thrown error that takes the Favorites tab down.
   */
  function buildAddToCartButton(item) {
    if (!item || !item.subject || item.subject.kind !== 'product') return null;
    var handle = item.subject.handle;
    var btn = el('button', 'pw-library__fav-cart', 'Add to cart');
    btn.type = 'button';
    btn.setAttribute('data-pw-fav-add-to-cart', '');
    btn.setAttribute('data-handle', handle);
    btn.addEventListener('click', function () {
      if (btn.disabled) return;
      btn.disabled = true;
      btn.textContent = 'Adding…';
      fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (product) {
          var variant = product && product.variants && product.variants[0];
          if (!variant) throw new Error('no variant');
          if (variant.available === false) {
            btn.textContent = 'Sold out';
            return null;
          }
          // Fix 2026-08-04 (Paul: "allowed us to add more than one quantity of an e-book").
          // This button was a standalone reimplementation that never got the live-cart
          // duplicate-check PR #89 (2026-07-27) established for the standard PDP/quick-add
          // path — every click here added another line regardless of what was already in the
          // cart. Same shape as that fix and pw-page-cart.js's original (PR #66): check /cart.js
          // before ever reaching /cart/add.js, block + "Already in cart" if the variant is
          // already present.
          btn.setAttribute('data-variant', String(variant.id));
          return fetch('/cart.js', { headers: { Accept: 'application/json' } })
            .then(function (res) { return res.ok ? res.json() : { items: [] }; })
            .catch(function () { return { items: [] }; })
            .then(function (cart) {
              var alreadyInCart = (cart.items || []).some(function (line) {
                return String(line.variant_id) === String(variant.id);
              });
              if (alreadyInCart) return { alreadyInCart: true };
              return fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }),
              }).then(function (res) {
                if (!res.ok) throw new Error('add failed');
                return { alreadyInCart: false };
              });
            });
        })
        .then(function (result) {
          if (!result) return; // sold out — already handled above
          if (result.alreadyInCart) {
            btn.textContent = 'Already in cart';
            setTimeout(function () {
              btn.textContent = 'Add to cart';
              btn.disabled = false;
            }, 2000);
            return;
          }
          btn.textContent = 'Added ✓';
          // Let the theme's cart drawer/count update the same way every other add does.
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          setTimeout(function () {
            btn.textContent = 'Add to cart';
            btn.disabled = false;
          }, 2000);
        })
        .catch(function () {
          // A failed add must say so — a button that silently does nothing reads as a broken site.
          btn.textContent = 'Try again';
          btn.disabled = false;
        });
    });
    return btn;
  }

  /**
   * Add to Cart on a PAGE-kind favourite (2026-08-11, Amy's confirmed items list item 1:
   * "the equivalent add-to-cart flow for individually-favorited PAGES — the page-cart tier
   * flow"). Supersedes the "pages get no cart button" boundary buildAddToCartButton's own
   * comment above documents (2026-08-02) — that call was correct THEN (no such flow existed
   * here yet), not permanently: the fix is to wire the SAME shared window.PWPageCart flow every
   * other single-page "Add" button in this theme already uses (pw-book-pages.js,
   * pw-image-search.js — assets/pw-page-cart.js), never a second, ad-hoc reimplementation that
   * could drift from the real tier arithmetic.
   *
   * `pageVariantId` is the tier-1 "Collage Art Page" variant, sourced from the section's own
   * `page_variant_id` setting (mirrors sections/pw-image-search.liquid's identical setting) —
   * blank (not configured) means no button at all, never a button that would POST an empty/wrong
   * variant.
   *
   * The data-* attributes are exactly what window.PWPageCart.addPageToCart() reads
   * (assets/pw-page-cart.js) AND what pw-in-cart.js's pageKeyFor() needs to mark this control by
   * book+page identity rather than by the shared tier variant id — every page-cart button on the
   * site posts the SAME static variant, so keying persistence off data-variant alone would light
   * up every page tile once any one page was in the cart. data-pw-add-page is what routes
   * pw-in-cart.js down the book+page path instead (see CONTROL_SELECTOR/pageKeyFor there).
   */
  function buildPageAddToCartButton(item, pageVariantId) {
    if (!item || !item.subject || item.subject.kind !== 'page' || !pageVariantId) return null;
    var subject = item.subject;
    var btn = el('button', 'pw-library__fav-cart', 'Add to cart');
    btn.type = 'button';
    btn.setAttribute('data-pw-fav-add-to-cart', '');
    btn.setAttribute('data-pw-add-page', '');
    btn.setAttribute('data-variant', pageVariantId);
    btn.setAttribute('data-parent-handle', subject.productHandle);
    btn.setAttribute('data-page-number', String(subject.pageNumber));
    btn.setAttribute('data-book-title', favoriteTitle(subject));
    if (item.thumbUrl) btn.setAttribute('data-cover-image', item.thumbUrl);
    btn.addEventListener('click', function () {
      if (window.PWPageCart) window.PWPageCart.addPageToCart(btn);
    });
    return btn;
  }

  /** Wrap a favourite's thumbnail in a link to its product page (Paul, 2026-08-02: "click on the
   *  image and be directed to the product page"). Returns the <a> so callers append one node. */
  function buildFavoriteThumbLink(item) {
    var link = el('a', 'pw-library__fav-thumb-link');
    link.href = window.PWFavorites.deepLink(item.subject);
    var thumb = el('img', 'pw-library__thumb');
    thumb.src = item.thumbUrl;
    thumb.alt = favoriteTitle(item.subject);
    thumb.loading = 'lazy';
    link.appendChild(thumb);
    return link;
  }

  function buildFavoriteCard(item, onRemove, pageVariantId) {
    var row = el('div', 'pw-library__item');
    if (item.thumbUrl) row.appendChild(buildFavoriteThumbLink(item));
    var meta = el('div', 'pw-library__meta');
    var link = el('a', 'pw-library__item-title', favoriteTitle(item.subject));
    link.href = window.PWFavorites.deepLink(item.subject);
    meta.appendChild(link);
    meta.appendChild(el('div', 'pw-library__item-sub', favoriteSubtitle(item.subject)));
    row.appendChild(meta);
    var actions = el('div', 'pw-library__fav-actions');
    var cart = buildAddToCartButton(item) || buildPageAddToCartButton(item, pageVariantId);
    if (cart) actions.appendChild(cart);
    actions.appendChild(buildRemoveButton(item, onRemove));
    row.appendChild(actions);
    return row;
  }

  /* Favorites, Phase 4 — a pure render function taking already-filtered/sorted items, exactly
   * mirroring renderOwned's shape (same sortByTitle/buildGridTile helpers, same view-mode
   * branch), rather than a parallel Favorites-only implementation. Filtering/sorting itself
   * lives in init()'s rerenderActive(), the one place both tabs' current search/sort/view
   * state is tracked. */
  /**
   * Resolve a favourite's thumbnail after the fact (2026-08-02, Paul: "Favorites does not load
   * thumbnail previews").
   *
   * Favourites that round-trip the server come back with no thumbUrl — `postFavoriteAdd` sends
   * only the subject and `mergeIfNeeded()` reads back the same. Rather than only fixing NEW
   * favourites, this looks the image up on demand and persists it, so the blank ones already in a
   * customer's list repair themselves the first time they view them.
   *
   * Two lookups because the catalogue has two shapes: a product has a live catalog read, a page
   * lives in the search index under its book handle + page number.
   *
   * Every failure is swallowed. A thumbnail is decoration; a favourite with no picture is a minor
   * blemish, a favourites tab that throws is a broken page.
   */
  function resolveFavoriteThumb(subject, backendUrl) {
    if (!subject || !backendUrl) return Promise.resolve(null);
    /**
     * PRODUCTS ONLY, and that is a measured limitation rather than an oversight.
     *
     * `/api/catalog-read?handle=…` returns `coverImage` at the top level — verified against
     * staging — so an eBook favourite resolves in one request. Both favourites in Paul's
     * 2026-08-02 report were eBooks, so this covers what was actually reported.
     *
     * PAGES CANNOT BE RESOLVED WITH TODAY'S API, checked rather than assumed:
     *   - `&page=N` is the PAGINATION parameter, not the page number — asking for
     *     `book=bird-nerd-ebook&page=4` returns page 35 first.
     *   - `&limit=100` is capped server-side at 24, and page 4 is not in that slice.
     *   - `pgmin`/`pgmax` are in the URL-param contract but are not honoured by the endpoint —
     *     total stayed at 143 either way.
     * Paginating to find one page would cost up to six requests for a 143-page book, and
     * hardcoding the R2 URL pattern here would bake an environment-specific host into the theme.
     * The right fix is a small lookup on the API; until then a page favourite keeps the
     * humanized-title tile it has always had, which is unchanged behaviour, not a regression.
     */
    if (subject.kind === 'page') return Promise.resolve(null);
    var url = backendUrl + '/api/catalog-read?handle=' + encodeURIComponent(subject.handle);
    return fetch(url)
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (data) {
        return (data && data.coverImage) || null;
      })
      .catch(function () {
        // A thumbnail is decoration. A favourites tab that throws is a broken page.
        return null;
      });
  }

  /** Fill in any missing favourite thumbnails, then re-render once if anything was found. */
  function backfillFavoriteThumbs(items, backendUrl, onDone) {
    if (!window.PWFavorites || !window.PWFavorites.setThumb) return;
    var pending = [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (!item.thumbUrl && item.subject && item.subject.kind !== 'page') pending.push(item);
    }
    if (!pending.length) return;
    var remaining = pending.length;
    var found = false;
    for (var j = 0; j < pending.length; j++) {
      (function (fav) {
        resolveFavoriteThumb(fav.subject, backendUrl).then(function (thumb) {
          if (thumb) {
            window.PWFavorites.setThumb(fav.subject, thumb);
            found = true;
          }
          remaining -= 1;
          // One re-render at the end, not one per resolved tile — a 40-item list would otherwise
          // rebuild the DOM 40 times and visibly thrash.
          if (remaining === 0 && found && typeof onDone === 'function') onDone();
        });
      })(pending[j]);
    }
  }

  function renderFavoritesList(container, items, viewMode, onRemove, pageVariantId) {
    if (!container) return;
    container.textContent = '';
    container.classList.toggle('pw-library__grid', viewMode === 'grid');
    var frag = document.createDocumentFragment();
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (viewMode === 'grid') {
        var actions = el('div', 'pw-library__fav-actions');
        var cartBtn = buildAddToCartButton(item) || buildPageAddToCartButton(item, pageVariantId);
        if (cartBtn) actions.appendChild(cartBtn);
        actions.appendChild(buildRemoveButton(item, onRemove));
        frag.appendChild(
          buildGridTile({
            title: favoriteTitle(item.subject),
            subtitle: favoriteSubtitle(item.subject),
            thumbUrl: item.thumbUrl || null,
            href: window.PWFavorites.deepLink(item.subject),
            actionEl: actions,
          }),
        );
      } else {
        frag.appendChild(buildFavoriteCard(item, onRemove, pageVariantId));
      }
    }
    container.appendChild(frag);
  }

  function itemSubtitle(ent) {
    if (ent.assetKind === 'page') {
      return ent.pageNumber != null ? 'Single Image · Page ' + ent.pageNumber : 'Single Image';
    }
    return 'eBook';
  }

  function downloadCountLabel(ent) {
    if (ent.maxDownloads == null) return '';
    if (ent.maxDownloads >= FREEBIE_MAX_DOWNLOADS) return 'Unlimited downloads';
    var used = ent.downloadsUsed || 0;
    var remaining = Math.max(0, ent.maxDownloads - used);
    return remaining + ' of ' + ent.maxDownloads + ' downloads left';
  }

  /* ---------------------------------------------------------------------------------------
   * Analytics (2026-08-20). This file emitted NOTHING until now, while seven sibling assets
   * (product-form, pw-auth, pw-favorites, pw-image-search, pw-lightbox,
   * pw-search-collection-analytics, ga-client-id-capture) already beacon to /api/collect.
   * That silence is why a 100%-silent download failure ran from 2026-06-05 to 2026-08-19 --
   * 47 burnt tokens across 38 customers -- with nothing to alert on.
   *
   * A native navigation can never report whether bytes actually landed, so the click is the
   * only observable moment. Emit there, PII-free (handle + assetKind only, never a customer
   * email), reusing the sibling pw_session_id / _ga convention so library events correlate
   * with the rest of the storefront.
   * ------------------------------------------------------------------------------------ */
  var PW_SESSION_KEY = 'pw_session_id';

  function pwSessionId() {
    try {
      var id = sessionStorage.getItem(PW_SESSION_KEY);
      if (!id) {
        id = 'pw_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
        sessionStorage.setItem(PW_SESSION_KEY, id);
      }
      return id;
    } catch (e) {
      return undefined;
    }
  }

  function pwGaClientId() {
    var m = /(?:^|;\s*)_ga=GA1\.\d\.(\d+\.\d+)/.exec(document.cookie || '');
    return m ? m[1] : undefined;
  }

  function postAnalyticsEvent(backendUrl, payload) {
    if (!backendUrl) return;
    try {
      var body = JSON.stringify(
        Object.assign({ eventVersion: 1, sessionId: pwSessionId(), clientId: pwGaClientId() }, payload),
      );
      var url = backendUrl + '/api/collect';
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      } else {
        fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true });
      }
    } catch (e) {
      /* an analytics beacon must never break a download */
    }
  }

  /* Native download (2026-08-20), replacing the fetch-and-buffer approach.
   *
   * `/api/download` ALREADY serves the native path: packages/api/src/download.ts's
   * `redirectServe()` answers with a 302 to a signed R2 URL, and both asset kinds carry
   * `Content-Disposition: attachment; filename="..."` (see download.ts / _deps.ts /
   * _runtime.ts). So a plain anchor navigation saves the file under the server's real name on
   * every browser -- which is why filenameFromResponse() is gone: there is no response to read.
   *
   * WHAT BROKE. The previous version called e.preventDefault(), fetched the URL, buffered the
   * whole PDF as a Blob (catalogue median ~33 MB, largest 443 MB) and .click()ed a synthetic
   * <a download> on an object URL. iOS/iPadOS Safari blocks a programmatic download click after
   * an async gap and fails WITHOUT throwing -- so .catch never ran, no status rendered, and the
   * button just reset to "Download". That is the exact reported symptom (2026-08-19: "it
   * indicated 'preparing' and then goes right back to 'download'").
   *
   * ONE REQUEST, ONE TOKEN. The buffering existed to avoid double-charging the budget:
   * executeDownload() commits a token as soon as io.serve() resolves, so fetching AND
   * navigating would bill two. Navigating only is a single request, hence a single commit.
   * Do NOT reintroduce a fetch() of ent.downloadUrl here.
   *
   * NO `download` ATTRIBUTE, deliberately (this is the BUG-26 constraint, PR #280). The HTML
   * download attribute forces the browser to save ANY response as a file regardless of
   * Content-Type, which would save the friendly deny page (exhausted budget / expired link /
   * deleted entitlement / invalid link) as a garbage file. Left off, a denial renders as the
   * on-brand page the backend already wrote, and a real file still downloads because the server
   * sets Content-Disposition. It is ignored for cross-origin URLs anyway (api.collagesoup.com
   * vs the storefront origin). */
  function downloadControl(ent, backendUrl) {
    // Both asset kinds download via the signed link the backend attaches to every item --
    // never ent.ebookUrl directly (that's the raw stored value, dead since P2.5 made the
    // original bucket private).
    var a = el('a', 'pw-library__download', 'Download');
    a.href = ent.downloadUrl;
    a.setAttribute('rel', 'noopener');
    a.setAttribute('data-entitlement-id', ent.id != null ? String(ent.id) : '');

    a.addEventListener('click', function () {
      if (a.getAttribute('aria-disabled') === 'true') return;
      postAnalyticsEvent(backendUrl, {
        event: 'library_download_click',
        handle: ent.productHandle || ent.bookHandle,
        assetKind: ent.assetKind,
        pageNumber: ent.assetKind === 'page' ? ent.pageNumber : undefined,
      });
      // No preventDefault(): the browser follows the href natively from here.
    });

    return a;
  }

  // Favorites/My-Library Phase 6 (2026-07-22): bulk ZIP select — page-kind items only, never
  // eBooks (they already download individually as a single file, no zip needed). Centralized
  // here so list view (buildItem) and grid view (buildGridTile, fixed 2026-07-24 — grid
  // previously rendered no checkbox at all, so the Select toggle silently did nothing for
  // anyone in grid view) can never drift apart on the assetKind==='page' gating.
  /* ------------------------------------------------------------------------------------------
   * SELECTION — PAGES ONLY. Settled 2026-08-02 after a device test.
   *
   * Mixed book + page selection was built this afternoon (Paul: "Selection Download is required
   * for cutover"; Amy §4), then REVERTED the same day on Paul's call after trying it on a phone:
   * "It just downloads the individual images as a zip file. Just turn off the tick boxes on full
   * ebooks and leave the messaging that you have to download them with the download button
   * individually."
   *
   * That is a product decision, not a defect report, and it is recorded here so nobody rebuilds
   * the mixed flow by accident: a selection that yields a zip of pages PLUS a separate book file
   * is confusing at the moment of download, whatever the copy says. Books have their own Download
   * button, which is unambiguous.
   *
   * THE DELIVERY RULE IS UNCHANGED and is why books were never zip-able in the first place: a
   * book is a single 100–400 MB PDF; zipping it beside twelve JPEGs would be slow to build, slow
   * to download, and would break resumable downloads for the item customers care most about.
   * ---------------------------------------------------------------------------------------- */

  /** ZIP_RULE.minItems on the server. Below it the zip route refuses anyway, and a one-file
   *  "bundle" is a worse download than the file itself. */
  var ZIP_MIN_ITEMS = 2;

  function pluralise(n, one) {
    return n + ' ' + (n === 1 ? one : one + 's');
  }

  /** What the customer reads on the bar: "4 pages", not "4 selected". */
  function describeSelection(pages) {
    return pages.length ? pluralise(pages.length, 'page') : 'nothing selected';
  }

  /** The zip URL plus the sentence shown BEFORE anything downloads. Pages only — the zip route is
   *  never widened to understand books, because that couples two mechanisms that fail differently. */
  function buildDownloadPlan(pageIds, backendUrl, token) {
    var t = encodeURIComponent(token || '');
    var ready = pageIds.length >= ZIP_MIN_ITEMS;
    return {
      zipUrl: ready ? backendUrl + '/api/download-zip?ids=' + pageIds.join(',') + '&token=' + t : null,
      ready: ready,
      explanation: ready
        ? 'Your ' + pluralise(pageIds.length, 'page') + ' will download together as one zip file. Books download on their own — use the Download button on the book itself.'
        : pageIds.length === 1
          ? 'Select at least one more page to download them together as a zip file.'
          : '',
    };
  }

  function buildZipCheckbox(ent, zipCtx) {
    // PAGES ONLY (see the block comment above — mixed selection was built and reverted the same
    // day, 2026-08-02, on Paul's device call). Books download via their own Download button.
    if (!(zipCtx && zipCtx.selectMode && ent && ent.assetKind === 'page' && ent.id != null)) return null;
    var checkbox = el('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'pw-library__zip-checkbox';
    checkbox.setAttribute('data-pw-zip-checkbox', '');
    checkbox.setAttribute('data-entitlement-id', String(ent.id));
    checkbox.checked = zipCtx.selected.has(String(ent.id));
    checkbox.addEventListener('change', function () {
      zipCtx.onToggle(String(ent.id), checkbox.checked);
    });
    return checkbox;
  }

  function buildItem(ent, backendUrl, zipCtx, statusEl) {
    var row = el('div', 'pw-library__item');

    var checkbox = buildZipCheckbox(ent, zipCtx);
    if (checkbox) row.appendChild(checkbox);

    if (ent.thumbUrl) {
      var thumb = el('img', 'pw-library__thumb');
      thumb.src = ent.thumbUrl;
      thumb.alt = '';
      thumb.loading = 'lazy';
      row.appendChild(thumb);
    }

    var meta = el('div', 'pw-library__meta');
    meta.appendChild(el('div', 'pw-library__item-title', resolveTitle(ent)));
    meta.appendChild(el('div', 'pw-library__item-sub', itemSubtitle(ent)));
    // Display-only (see isDelisted): never a gate, only a label. The Download control below is
    // built identically either way.
    if (isDelisted(ent)) meta.appendChild(delistedNote());
    var countLabel = downloadCountLabel(ent);
    if (countLabel) meta.appendChild(el('div', 'pw-library__item-count', countLabel));
    row.appendChild(meta);

    row.appendChild(downloadControl(ent, backendUrl));

    // Favorites/My-Library Phase 5 (2026-07-22): owned-book "view pages" preview.
    if (hasRealBookHandle(ent) && backendUrl) {
      var wrap = el('div', 'pw-library__item-wrap');
      wrap.appendChild(row);
      wrap.appendChild(buildBookPreviewToggle(ent, backendUrl));
      return wrap;
    }
    return row;
  }

  /* Only ebooks with a real (non-numeric, non-empty) bookHandle are eligible — legacy rows
   * can carry a numeric fallback handle (same class of data-quality guard as isJunkTitle). */
  function hasRealBookHandle(ent) {
    if (ent.assetKind !== 'ebook') return false;
    var handle = ent.bookHandle;
    if (!handle) return false;
    if (/^\d+$/.test(handle)) return false;
    return true;
  }

  function buildBookPreviewTile(result) {
    var tile = el('div', 'pw-library__preview-tile');
    if (result.coverImage) {
      var img = el('img');
      img.src = result.coverImage;
      img.alt = result.title || '';
      img.loading = 'lazy';
      tile.appendChild(img);
    }
    var label = result.pageNumber != null ? 'Page ' + result.pageNumber : result.title || 'Page';
    tile.appendChild(el('p', 'pw-library__preview-caption', label));
    return tile;
  }

  /* "View pages" toggle + grid for an owned ebook. Reuses the EXACT same endpoint contract
   * blocks/pw-see-all-pages.liquid's PDP grid already calls ({backend}/api/search?type=
   * page&book=<handle>&page=<n>) — no new backend route. Unlike the PDP grid, renders NO
   * add-to-cart controls (the owner already owns every page in a book they bought) and
   * applies no client-side hidden/cover-page filter — the endpoint already excludes those
   * server-side, so duplicating that logic client-side risks drifting out of sync. */
  function buildBookPreviewToggle(ent, backendUrl) {
    var wrap = el('div', 'pw-library__preview');
    var toggle = el('button', 'pw-library__preview-toggle', 'View pages');
    toggle.type = 'button';
    toggle.setAttribute('data-pw-view-pages', '');
    toggle.setAttribute('aria-expanded', 'false');
    var status = el('p', 'pw-library__preview-status', '');
    status.hidden = true;
    var grid = el('div', 'pw-library__preview-grid');
    grid.setAttribute('data-pw-book-preview', '');
    grid.hidden = true;
    var moreBtn = el('button', 'button button-secondary pw-library__preview-more', 'Load more pages');
    moreBtn.type = 'button';
    moreBtn.hidden = true;

    var loaded = false;
    var nextPage = 1;
    var totalPages = 1;

    function endpoint(pageNum) {
      return backendUrl + '/api/search?type=page&book=' + encodeURIComponent(ent.bookHandle) + '&page=' + pageNum;
    }

    function loadPage(pageNum) {
      status.hidden = false;
      status.textContent = 'Loading pages…';
      return fetch(endpoint(pageNum), { headers: { Accept: 'application/json' } })
        .then(function (resp) {
          if (!resp.ok) throw new Error('HTTP ' + resp.status);
          return resp.json();
        })
        .then(function (data) {
          var list = (data && Array.isArray(data.results) ? data.results : []).filter(function (r) {
            return r && r.type === 'page';
          });
          if (pageNum === 1 && list.length === 0) {
            // No page docs indexed for this book (2026-08-30, Kathy Oltmanns ticket) -- true
            // for most short freebies by design (TASKS.md WISHLIST "Let owners VIEW freebie
            // pages", a separate deferred content-policy call, not a quick fix here). This used
            // to hide both elements the instant the customer clicked -- a real, empty result
            // read as "nothing comes up". Leave the toggle visible but disabled and say so.
            toggle.disabled = true;
            toggle.setAttribute('aria-expanded', 'false');
            status.hidden = false;
            status.textContent = 'No page previews for this item -- the file itself is still fully downloadable above.';
            return;
          }
          totalPages = (data && data.pages) || 1;
          var frag = document.createDocumentFragment();
          for (var i = 0; i < list.length; i++) frag.appendChild(buildBookPreviewTile(list[i]));
          grid.appendChild(frag);
          nextPage = pageNum + 1;
          status.hidden = true;
          grid.hidden = false;
          moreBtn.hidden = nextPage > totalPages;
        })
        .catch(function () {
          status.hidden = false;
          status.textContent = 'Something went wrong loading pages.';
        });
    }

    toggle.addEventListener('click', function () {
      if (!loaded) {
        loaded = true;
        toggle.setAttribute('aria-expanded', 'true');
        loadPage(1);
        return;
      }
      var willOpen = grid.hidden;
      grid.hidden = !willOpen;
      moreBtn.hidden = !willOpen || nextPage > totalPages;
      toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    });

    moreBtn.addEventListener('click', function () {
      if (nextPage <= totalPages) loadPage(nextPage);
    });

    wrap.appendChild(toggle);
    wrap.appendChild(status);
    wrap.appendChild(grid);
    wrap.appendChild(moreBtn);
    return wrap;
  }

  // Favorites, Phase 4 (2026-07-21) — sort + search + grid/list view, shared identically by
  // both the Library and Favorites tabs (never a parallel per-tab implementation).

  var VIEW_MODE_KEY = 'pw_library_view_mode';

  function currentViewMode() {
    try {
      return localStorage.getItem(VIEW_MODE_KEY) === 'grid' ? 'grid' : 'list';
    } catch (e) {
      return 'list';
    }
  }

  function setViewMode(mode) {
    try {
      localStorage.setItem(VIEW_MODE_KEY, mode);
    } catch (e) {
      /* storage full/blocked — view choice just won't persist across reload */
    }
  }

  /* Newest (default) = pass through in whatever order the caller already has (today's implicit
   * backend/local order); A–Z sorts by titleFn's output — the SAME resolver the caller's own
   * search already uses, so sort and search can never disagree about a title. */
  function sortByTitle(items, titleFn, sortMode) {
    if (sortMode !== 'az') return items;
    var copy = items.slice();
    copy.sort(function (a, b) {
      /* NUMERIC collation (Amy, 2026-08-01: "not sorted numerically ..1,10,100"). The strings
       * compared here are titles like "Single Image · Page 10", so a plain localeCompare orders
       * them 1, 10, 100, 11, 2. `numeric: true` makes runs of digits compare as numbers, which
       * fixes page ordering without changing how ordinary titles sort. Only visible past 9 owned
       * pages in one book, which is why it survived this long. */
      return titleFn(a).localeCompare(titleFn(b), undefined, { numeric: true, sensitivity: 'base' });
    });
    return copy;
  }

  /* Shared grid-view tile, used by both the Library (thumbUrl from the entitlement) and
   * Favorites (no thumbUrl — WishlistItem carries no metadata) render paths. Falls back to
   * humanizeHandle's already-established humanized-title treatment when there's no image. */
  function buildGridTile(opts) {
    var card = el('div', 'pw-library__grid-item');
    var thumbWrap = el('div', 'pw-library__grid-thumb-wrap');
    // `opts.href` (Favorites, 2026-08-02) makes the image itself the link to the product page —
    // Paul: "click on the image and be directed to the product page." The Library tab passes no
    // href, so its tiles are unchanged (their action is Download, not navigation).
    var imageHost = thumbWrap;
    if (opts.href) {
      var thumbLink = el('a', 'pw-library__fav-thumb-link');
      thumbLink.href = opts.href;
      thumbWrap.appendChild(thumbLink);
      imageHost = thumbLink;
    }
    if (opts.thumbUrl) {
      var img = el('img');
      img.src = opts.thumbUrl;
      img.alt = opts.href ? opts.title : '';
      img.loading = 'lazy';
      imageHost.appendChild(img);
    } else {
      imageHost.appendChild(el('span', 'pw-library__grid-thumb--empty', opts.title));
    }
    // Overlaid on the thumbnail corner, same checkbox element buildZipCheckbox() builds for
    // list view — only present when opts.checkbox was actually passed (Favorites tiles never
    // pass one, same as buildItem's gating). Appended to the WRAP, never inside the link, so a
    // tap on the tick can never navigate away mid-selection.
    if (opts.checkbox) thumbWrap.appendChild(opts.checkbox);
    card.appendChild(thumbWrap);
    if (opts.href) {
      var titleLink = el('a', 'pw-library__item-title', opts.title);
      titleLink.href = opts.href;
      card.appendChild(titleLink);
    } else {
      card.appendChild(el('div', 'pw-library__item-title', opts.title));
    }
    card.appendChild(el('div', 'pw-library__item-sub', opts.subtitle));
    // Same delisted label list view shows (renderOwned passes it) -- the two views must not
    // disagree about whether an item is still sold.
    if (opts.retired) card.appendChild(delistedNote());
    card.appendChild(opts.actionEl);
    return card;
  }

  function renderOwned(container, items, viewMode, backendUrl, zipCtx, statusEl) {
    container.textContent = '';
    container.classList.toggle('pw-library__grid', viewMode === 'grid');
    var frag = document.createDocumentFragment();
    for (var i = 0; i < items.length; i++) {
      var ent = items[i];
      if (!ent) continue;
      if (viewMode === 'grid') {
        frag.appendChild(
          buildGridTile({
            title: resolveTitle(ent),
            subtitle: itemSubtitle(ent),
            thumbUrl: ent.thumbUrl,
            retired: isDelisted(ent),
            actionEl: downloadControl(ent, backendUrl),
            checkbox: buildZipCheckbox(ent, zipCtx),
          }),
        );
      } else {
        frag.appendChild(buildItem(ent, backendUrl, zipCtx, statusEl));
      }
    }
    container.appendChild(frag);
  }

  /* Wire up the "request a library link" form (visitors with no usable token). Binding is
   * idempotent-safe per root since init() itself only runs once (guarded by data-pw-init). */
  function initRequestForm(root, backendUrl) {
    var form = root.querySelector('[data-pw-request-form]');
    if (!form) return;
    var emailInput = form.querySelector('[data-pw-request-email]');
    var requestBtn = form.querySelector('[data-pw-request-btn]');
    var requestStatus = root.querySelector('[data-pw-request-status]');

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = emailInput && emailInput.value ? emailInput.value.trim() : '';
      if (!email) return;

      if (!backendUrl) {
        setStatus(requestStatus, 'My Library is not configured.');
        return;
      }

      if (requestBtn) requestBtn.disabled = true;
      setStatus(requestStatus, 'Sending…');

      fetch(backendUrl + '/api/library-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email: email }),
      })
        .then(function (resp) {
          // Deliberately non-revealing: the 200 message is identical whether or not the
          // address actually has any purchases on file — never branch the copy on that.
          if (resp.status === 200) {
            setStatus(requestStatus, 'If that email has purchases, a link is on its way — check your inbox.');
          } else if (resp.status === 429) {
            setStatus(requestStatus, 'Too many requests — try again in a bit.');
          } else {
            setStatus(requestStatus, 'Something went wrong — please try again.');
          }
        })
        .catch(function () {
          setStatus(requestStatus, 'Something went wrong — please try again.');
        })
        .then(function () {
          if (requestBtn) requestBtn.disabled = false;
        });
    });
  }

  // My Library Shopify-login bridge, Phase 2 (2026-07-14) — the app-proxy path Shopify signs and
  // forwards to GET /api/library-session (see pagewright-library-proxy-app/shopify.app.toml's
  // [app_proxy] block). Same-origin (rides the customer's real Shopify session cookies
  // automatically) — never called cross-origin against `backendUrl` directly.
  var SESSION_BRIDGE_PATH = '/apps/library';

  function init(root) {
    if (!root || root.dataset.pwInit === 'true') return;
    root.dataset.pwInit = 'true';

    // FIX-UP (2026-09-01, Sonnet #1 -- MEDIUM): locationTokenChecked/locationTokenCached are
    // module-scope (see their declaration above readTokenFromLocation()), so without a reset here
    // a `shopify:section:load` theme-editor re-init (line ~1940 below) -- which calls init() again
    // against a FRESH `pw-library` DOM node, without a real page navigation -- would replay the
    // ORIGINAL location token as fromLocation:true even after THIS load already stripped the URL
    // and (if applicable) already resolved the interstitial once: a visitor who already clicked
    // "Show my library" could see the interstitial reappear on a section reload, or (in any mode)
    // trigger a duplicate /api/library fetch and a duplicate identity-check beacon. Resetting the
    // memo at the top of every init() call (the smaller, cleaner fix vs. re-scoping the read
    // entirely) makes each init() call re-parse window.location.href fresh -- correct, since by
    // the time a section reload happens the URL has already been stripped for real.
    locationTokenChecked = false;
    locationTokenCached = undefined;

    var backendUrl = (root.dataset.backendUrl || '').replace(/\/+$/, '');
    var loggedInCustomerId = root.dataset.customerId || '';
    // Favorites, page-cart tier flow (2026-08-11, item 1) — the tier-1 "Collage Art Page"
    // variant id, sourced from the section's own page_variant_id setting exactly like
    // sections/pw-image-search.liquid's identical setting. Blank = the setting isn't configured
    // on this section instance, which buildPageAddToCartButton treats as "render no button"
    // rather than posting an empty/wrong variant.
    var pageVariantId = root.dataset.pageVariantId || '';
    // tasks.db #816 (2026-09-01) -- the real, Liquid-rendered session identity. customer.email is
    // trustworthy ONLY here (server-verified render), same posture as customer.id above.
    var loggedInCustomerEmail = root.dataset.customerEmail || '';
    var identityBar = root.querySelector('[data-pw-identity-bar]');
    var interstitialEl = root.querySelector('[data-pw-interstitial]');
    var interstitialBodyEl = interstitialEl && interstitialEl.querySelector('[data-pw-interstitial-body]');
    var interstitialShowMineBtn = root.querySelector('[data-pw-interstitial-show-mine]');
    var interstitialViewOtherBtn = root.querySelector('[data-pw-interstitial-view-other]');
    // Favorites/In-Cart tabs read storage directly (pw-favorites.js's own currentToken()), so
    // during a "View someone else's library" render (which never touches storage) they would
    // otherwise show the VISITOR's own favorites/cart state side-by-side with someone else's
    // library — round-2 Sonnet SF, tests i4. The Library tab itself has no in-cart controls, so
    // hiding the Favorites tab (its panel is the only place any in-cart-state buttons render on
    // this page) covers both.
    var favoritesTabBtn = root.querySelector('[data-pw-tab="favorites"]');
    var signinEl = root.querySelector('[data-pw-signin]');
    var pendingEl = root.querySelector('[data-pw-pending]');
    var pendingTimeoutEl = root.querySelector('[data-pw-pending-timeout]');
    var requestWrap = root.querySelector('[data-pw-request-wrap]');
    var ownedWrap = root.querySelector('[data-pw-owned-wrap]');
    var owned = root.querySelector('[data-pw-owned]');
    var empty = root.querySelector('[data-pw-empty]');
    var status = root.querySelector('[data-pw-status]');
    var controlsWrap = root.querySelector('[data-pw-controls-wrap]');
    var searchInput = root.querySelector('[data-pw-search]');
    var searchEmpty = root.querySelector('[data-pw-search-empty]');
    var sortSelect = root.querySelector('[data-pw-sort]');
    var viewButtons = root.querySelectorAll('[data-pw-view]');
    var allItems = [];
    // Guards a single session-bridge retry on a stale/expired token (see loadLibrary's 401/403
    // handler below) — without this, a persistently-failing bridge mint could recurse forever.
    // tasks.db #815 audit follow-up (2026-09-01, Sonnet MEDIUM): this guard is now set at EVERY
    // place a bridge-obtained token is handed to loadLibrary() — trySessionBridge()'s own success
    // handler and pollBridgeOnce()'s success branch, in addition to the two loadLibrary()-internal
    // retry branches below — so "the bridge has already been tried this load" is true regardless
    // of which of those four call paths obtained the token.
    var bridgeRetried = false;
    // PR-4 (2026-08-13/14, login-forensics defect #6) — bounded backoff poll for a known customer
    // whose library is empty because their entitlement mint (async orders/paid webhook) hasn't
    // landed yet. ~4m15s total, covering the normal 5s-5min lag. pollBridgeOnce() itself never
    // calls trySessionBridge directly, and since tasks.db #815 (2026-09-01) it also sets
    // bridgeRetried before handing its token to loadLibrary() — so a poll-obtained token that then
    // 401s no longer gets an extra trySessionBridge() retry (previously a real, if bounded,
    // indirect path found in 2026-08-14 review); it now falls straight to showNoToken(), which
    // calls schedulePendingPoll() again immediately. Still bounded either way; pendingResolved
    // below exists specifically so a poll that already succeeded can never be restarted by that
    // path. Once the delays array is exhausted with still no token (the rare multi-hour-incident
    // case), showPendingTimeout() swaps the copy — the "this page updates automatically" claim
    // must not outlive the thing actually checking for updates.
    var PENDING_DELAYS_MS = [5000, 10000, 20000, 40000, 60000, 60000, 60000];
    var pendingAttempt = 0;
    var pendingTimer = null;
    // Guards the fetch window itself (pendingTimer only guards the gap BETWEEN scheduling and
    // firing) — without this, a showNoToken() call arriving while a poll request is already in
    // flight would start a second, concurrent poll chain: two overlapping bridge fetches, both
    // able to store a token and load the library.
    var pendingInFlight = false;
    // Set once a poll succeeds. Distinct from pendingAttempt reaching the array length (which
    // means "gave up", not "succeeded") — checked first in schedulePendingPoll so a library that
    // already rendered can never have its poll chain restarted.
    var pendingResolved = false;

    // Favorites, Phase 3/4 (2026-07-21) — Library/Favorites tabs, and the shared search/sort/
    // view controls (one instance, both tabs — never a parallel per-tab implementation).
    var tabButtons = root.querySelectorAll('[data-pw-tab]');
    var tabPanels = root.querySelectorAll('[data-pw-panel]');
    var favoritesList = root.querySelector('[data-pw-favorites-list]');
    var favoritesEmpty = root.querySelector('[data-pw-favorites-empty]');
    var favoritesSearchEmpty = root.querySelector('[data-pw-favorites-search-empty]');
    var activeTab = 'library';
    var currentSort = 'newest';
    var viewMode = currentViewMode();

    // Favorites/My-Library Phase 6 (2026-07-22) — bulk ZIP download. Select mode + checkboxes
    // apply to owned PAGE items only, list view, Library tab only (never eBooks, never grid
    // view, never the Favorites tab — see buildItem's own gating).
    var zipSelectModeBtn = root.querySelector('[data-pw-zip-select-mode]');
    var zipBar = root.querySelector('[data-pw-zip-bar]');
    var zipCount = root.querySelector('[data-pw-zip-count]');
    var zipSelectAllBtn = root.querySelector('[data-pw-zip-select-all]');
    var zipDownloadLink = root.querySelector('[data-pw-zip-download]');
    // Selection Download (2026-08-02): the sentence shown before anything starts, and the list of
    // individually-delivered files. Both null-safe — show()'s own guard means a theme without
    // this markup keeps the pre-2026-08-02 behaviour rather than throwing.
    var zipExplain = root.querySelector('[data-pw-zip-explain]');
    var zipList = root.querySelector('[data-pw-zip-list]');
    var zipSelectMode = false;
    var zipSelected = new Set();

    /**
     * The selection bar. Pages only (see the SELECTION block comment at the top of this file);
     * it counts in the customer's terms and states what will happen before anything starts.
     */
    function updateZipBar() {
      var pageIds = Array.from(zipSelected);
      var n = pageIds.length;
      show(zipBar, zipSelectMode && n > 0);

      var plan = buildDownloadPlan(pageIds, backendUrl, currentToken());

      if (zipCount) zipCount.textContent = describeSelection(pageIds);
      if (zipExplain) {
        zipExplain.textContent = plan.explanation;
        show(zipExplain, Boolean(plan.explanation) && n > 0);
      }
      if (zipDownloadLink) {
        zipDownloadLink.setAttribute('aria-disabled', plan.ready ? 'false' : 'true');
        zipDownloadLink.href = plan.zipUrl || '#';
      }
    }

    // BUG-26 client-side follow-up (2026-08-19) — this bulk ZIP link is a plain navigation.
    // As of 2026-08-20 downloadControl() above is one too, so the two now agree rather than
    // diverging; what follows is why the ZIP link never needed the fetch-first detour at all.
    // Two real differences make it a separate concern, not an oversight:
    //   1. It never had a `download` attribute in the first place (see the plain <a> in
    //      sections/pw-library.liquid) — the bug this task fixes (`download` forcing a save
    //      regardless of Content-Type) doesn't apply here. A denied zip's JSON error response
    //      just renders as text in the tab today, same as it always has.
    //   2. /api/download-zip (packages/api/src/download-zip.ts) never got PR #280's
    //      wantsHtml()/denyPage() treatment — it still always returns plain JSON on denial
    //      (`error()`), regardless of Accept header. There is no nicer HTML page to fetch for
    //      here yet, so adding fetch-first now would add complexity for zero customer-visible
    //      benefit. Revisit if/when the backend adds an HTML deny page for this endpoint too.

    function zipCtx() {
      return {
        selectMode: zipSelectMode,
        selected: zipSelected,
        onToggle: function (id, checked) {
          if (checked) zipSelected.add(id);
          else zipSelected.delete(id);
          updateZipBar();
        },
      };
    }

    if (zipSelectModeBtn) {
      zipSelectModeBtn.addEventListener('click', function () {
        zipSelectMode = !zipSelectMode;
        zipSelectModeBtn.setAttribute('aria-pressed', zipSelectMode ? 'true' : 'false');
        if (!zipSelectMode) {
          zipSelected.clear();
          updateZipBar();
        }
        renderLibraryTab();
      });
    }

    function selectAllZippablePages() {
      for (var i = 0; i < allItems.length; i++) {
        var it = allItems[i];
        if (it && it.assetKind === 'page' && it.id != null) zipSelected.add(String(it.id));
      }
      updateZipBar();
      renderLibraryTab();
    }

    if (zipSelectAllBtn) {
      zipSelectAllBtn.addEventListener('click', selectAllZippablePages);
    }

    function show(node, on) {
      if (node) node.hidden = !on;
    }

    /*
     * Identity bar (2026-08-18, Karen Klingborg incident) — decodes the RESOLVED token directly,
     * via window.PWIdentity (assets/pw-identity.js, loaded from the site header). Called from the
     * top of loadLibrary() so it covers every path a token can arrive by (URL param, stored
     * token, session-bridge mint, pending-poll mint) from one place, and renders BEFORE the
     * /api/library call even starts — no dependency on that call succeeding.
     *
     * Reuses this file's OWN clearToken()/showNoToken() for "Not you?" rather than
     * window.PWIdentity.clear() + a redirect (the header badge's approach): we're already ON the
     * page that can show the signed-out state in place, so do that instead of a round-trip reload.
     *
     * FIX-UP (2026-09-01, Opus S1 -- SHOULD_FIX): `viewOnly` (true for the interstitial's "View
     * someone else's library" render, §2.4.5) used to be silently ignored here -- this bar showed
     * the ATTACKER's full, UNMASKED email (undoing the interstitial's own masking work) right next
     * to a "Not you?" button whose handler was a bare clearToken(), which wipes the VICTIM's own
     * real pw_library_token/pw_owned_handles/pw_favorites_last_merged_token -- a destructive action
     * against the wrong person's credential, with a label that actively invites the click since it
     * genuinely isn't the visitor's own identity being shown. Fixed: during a view-only render, the
     * email is passed through maskEmail() (the same masking the interstitial itself uses) and the
     * action becomes "Back to my library", reusing proceedWithOwnSession() -- the SAME mechanism
     * the interstitial's own primary button uses to return to the visitor's real session -- rather
     * than clearing anything belonging to whoever is actually signed in on this device.
     */
    function renderIdentityBar(token, viewOnly) {
      if (!identityBar) return;
      var email = window.PWIdentity && window.PWIdentity.decodeEmail ? window.PWIdentity.decodeEmail(token) : null;
      if (!email) {
        show(identityBar, false);
        identityBar.textContent = '';
        return;
      }
      identityBar.textContent = '';
      var displayEmail = viewOnly ? maskEmail(email) : email;
      var label = el('span', 'pw-library__identity-email', 'Signed in as ' + displayEmail);
      var switchBtnText = viewOnly ? 'Back to my library' : 'Not you?';
      var switchBtn = el('button', 'pw-library__identity-switch', switchBtnText);
      switchBtn.type = 'button';
      switchBtn.setAttribute('data-pw-identity-switch', '');
      switchBtn.setAttribute('aria-label', viewOnly ? 'Back to my library' : 'Not you? Sign in with a different email');
      switchBtn.addEventListener('click', function () {
        if (viewOnly) {
          proceedWithOwnSession();
        } else {
          clearToken();
          showNoToken();
        }
      });
      identityBar.appendChild(label);
      identityBar.appendChild(switchBtn);
      show(identityBar, true);
    }

    /*
     * tasks.db #816 (2026-09-01, PLAN v3 §2.4.5) -- the location-token identity mismatch
     * interstitial. `setFavoritesTabHidden` also covers the round-2 Sonnet SF finding (test i4):
     * hide/restore is the ONLY thing this file changes for that gap — pw-favorites.js/
     * pw-in-cart.js read storage directly and are never touched.
     */
    function setFavoritesTabHidden(hiddenFlag) {
      if (favoritesTabBtn) show(favoritesTabBtn, !hiddenFlag);
      if (hiddenFlag && activeTab === 'favorites') setActiveTab('library');
    }

    function hideInterstitial() {
      show(interstitialEl, false);
    }

    /* Fills the locale-sourced body template's `__SESSION_EMAIL__`/`__MASKED_EMAIL__` literal
     * sentinel placeholders (see sections/pw-library.liquid's data-template attribute and
     * locales/en.default.json) with the real session email and the client-masked token email --
     * the masked value only exists once JS decodes the token, so Liquid can never render it.
     *
     * FIX-UP (2026-09-01, Opus M1 -- HARD BLOCKER): these placeholders used to be Liquid-SHAPED
     * (`{{SESSION_EMAIL}}`/`{{MASKED_EMAIL}}`), which required the .liquid template to embed
     * literal `{{ }}` inside a Liquid string-literal filter argument -- something Liquid's
     * regex-based tokenizer cannot parse (it mangled the whole tag; `shopify theme check` reported
     * `UndefinedObject -- Unknown object 'MASKED_EMAIL' used.`). The sentinels are now plain text
     * with no braces at all, so they can never collide with Liquid's own tokenizer; the .liquid
     * file takes no variable substitution in Liquid at all (`| t | escape`, no `t:` args) and this
     * function does the entire substitution. */
    function renderInterstitialBody(sessionEmail, tokenEmail) {
      if (!interstitialBodyEl) return;
      var tpl = interstitialBodyEl.getAttribute('data-template') || '';
      interstitialBodyEl.textContent = tpl
        .split('__SESSION_EMAIL__').join(sessionEmail || '')
        .split('__MASKED_EMAIL__').join(maskEmail(tokenEmail));
    }

    /* Primary interstitial action: "Show my library". Discards the URL token ENTIRELY — never
     * stored, never passed to loadLibrary — and proceeds exactly as if the visitor arrived with
     * no link at all: an existing stored token, the session bridge, or the sign-in wall. */
    function proceedWithOwnSession() {
      hideInterstitial();
      setFavoritesTabHidden(false);
      var stored;
      if (window.PWAuth) {
        stored = window.PWAuth.getToken();
      } else {
        try {
          stored = sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY) || undefined;
        } catch (e) {
          stored = undefined;
        }
      }
      if (!stored) {
        if (loggedInCustomerId) {
          trySessionBridge();
        } else {
          showNoToken();
        }
        return;
      }
      show(signinEl, false);
      show(requestWrap, false);
      loadLibrary(stored);
    }

    /* Secondary interstitial action: "View someone else's library (read-only)". Renders for THIS
     * page load only via the allowPersist:false gate on loadLibrary() (§2.4.4) — no storeToken(),
     * no localStorage, no sessionStorage, including for any sliding-renewal response. */
    function proceedViewOnly(token) {
      hideInterstitial();
      setFavoritesTabHidden(true);
      show(signinEl, false);
      show(requestWrap, false);
      loadLibrary(token, { allowPersist: false });
    }

    function showIdentityInterstitial(token, claims, sessionEmail) {
      if (!interstitialEl) {
        // No interstitial markup present (an un-upgraded cached section) -- fail SAFE to the
        // least-trust option rather than silently rendering a foreign library.
        proceedWithOwnSession();
        return;
      }
      renderInterstitialBody(sessionEmail, claims && claims.email);
      show(interstitialEl, true);
      if (interstitialShowMineBtn) {
        interstitialShowMineBtn.addEventListener('click', proceedWithOwnSession);
      }
      if (interstitialViewOtherBtn) {
        interstitialViewOtherBtn.addEventListener('click', function () {
          proceedViewOnly(token);
        });
      }
    }

    function updateControlsVisibility() {
      // Favorites is always locally available (even zero items), so its controls stay visible;
      // Library only shows controls once there's something fetched worth searching/sorting.
      show(controlsWrap, activeTab === 'favorites' || allItems.length > 0);
    }

    /* Re-render the Library tab from the full fetched list, filtered/sorted by the current
     * shared controls state — never re-fetches; the whole library already came down in one
     * /api/library call. */
    function renderLibraryTab() {
      var query = searchInput && searchInput.value ? searchInput.value.trim() : '';
      var matches = [];
      for (var i = 0; i < allItems.length; i++) {
        if (itemMatchesQuery(allItems[i], query)) matches.push(allItems[i]);
      }
      matches = sortByTitle(matches, resolveTitle, currentSort);
      if (query && matches.length === 0) {
        show(searchEmpty, true);
        renderOwned(owned, [], viewMode, backendUrl, zipCtx(), status);
      } else {
        show(searchEmpty, false);
        renderOwned(owned, matches, viewMode, backendUrl, zipCtx(), status);
      }
      updateZipBar();
    }

    /* Renders from PWFavorites' current (already-merged) local store — never a separate fetch;
     * mergeIfNeeded() is awaited by setActiveTab before this first runs. Shares sortByTitle/
     * buildGridTile with renderLibraryTab, not a parallel implementation (dev/TDD plan Phase 4
     * item 5). */
    function renderFavoritesTab() {
      if (!window.PWFavorites) return;
      var query = searchInput && searchInput.value ? searchInput.value.trim() : '';
      var items = window.PWFavorites.getFavorites();
      var matches = query
        ? items.filter(function (item) {
            return favoriteTitle(item.subject).toLowerCase().indexOf(query.toLowerCase()) !== -1;
          })
        : items;
      matches = sortByTitle(
        matches,
        function (item) {
          return favoriteTitle(item.subject);
        },
        currentSort,
      );
      if (favoritesEmpty) favoritesEmpty.hidden = items.length > 0;
      if (favoritesSearchEmpty) favoritesSearchEmpty.hidden = !(query && matches.length === 0);
      renderFavoritesList(favoritesList, matches, viewMode, renderFavoritesTab, pageVariantId);
      // Fill in thumbnails the server sync could not carry, then re-render once. Guarded so the
      // re-render cannot recurse: by then every resolved favourite HAS a thumbUrl, so the second
      // pass finds nothing pending and stops.
      backfillFavoriteThumbs(matches, backendUrl, renderFavoritesTab);
    }

    function rerenderActive() {
      if (activeTab === 'favorites') {
        renderFavoritesTab();
      } else {
        renderLibraryTab();
      }
    }

    function setActiveTab(name) {
      activeTab = name;
      tabButtons.forEach(function (btn) {
        var active = btn.getAttribute('data-pw-tab') === name;
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      tabPanels.forEach(function (panel) {
        panel.hidden = panel.getAttribute('data-pw-panel') !== name;
      });
      updateControlsVisibility();
      // Never re-fetches the Library list (allItems stays whatever loadLibrary already fetched)
      // — switching tabs only toggles panel visibility. The Favorites tab merges (if a token
      // exists) then renders from PWFavorites' local store, no backend fetch of its own either.
      if (name === 'favorites' && window.PWFavorites) {
        window.PWFavorites.mergeIfNeeded().then(renderFavoritesTab);
      } else {
        renderLibraryTab();
      }
    }

    tabButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        setActiveTab(btn.getAttribute('data-pw-tab'));
      });
    });

    if (searchInput) searchInput.addEventListener('input', rerenderActive);
    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        currentSort = sortSelect.value === 'az' ? 'az' : 'newest';
        rerenderActive();
      });
    }
    viewButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        viewMode = btn.getAttribute('data-pw-view') === 'grid' ? 'grid' : 'list';
        setViewMode(viewMode);
        viewButtons.forEach(function (b) {
          b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
        });
        rerenderActive();
      });
    });
    viewButtons.forEach(function (btn) {
      btn.setAttribute('aria-pressed', btn.getAttribute('data-pw-view') === viewMode ? 'true' : 'false');
    });

    function initialTab() {
      try {
        return new URLSearchParams(window.location.search).get('tab') === 'favorites'
          ? 'favorites'
          : 'library';
      } catch (e) {
        return 'library';
      }
    }

    // No usable token (missing now, or turns out expired/invalid below). signinEl (data-pw-signin)
    // was a vestigial, always-null lookup from 2026-07-14 (when the old sign-in-wall element it
    // toggled was removed from this theme) until the Option B two-card redesign (2026-09-04) gave
    // a real element — the new "Have an account?" card in sections/pw-library.liquid's
    // request-wrap — this exact data-attribute, making this call live again with NO logic change
    // here: same show(signinEl, …) call, same null-safe show() guard, now just pointed at a real
    // node. The REAL guest-vs-customer distinction still lives primarily in this section's Liquid
    // ({% unless customer %} around the guest-path status line, {% if customer %} around the
    // pending copy) — a customer Liquid KNOWS is signed in never sees signed-out copy or the
    // sign-in card, server-rendered/JS-gated independent of one another (PR-4, login-forensics
    // defect #6: the bridge's noEntitlements:true can't distinguish "purchase still minting" from
    // "never purchased", so showing signed-out copy here would be an outright lie for the first
    // case). They get the pending copy instead, and a bounded poll picks up the entitlement once
    // the async orders/paid webhook lands. The library API is never called without a token.
    function showNoToken() {
      show(identityBar, false);
      identityBar && (identityBar.textContent = '');
      show(signinEl, !loggedInCustomerId);
      show(pendingEl, !!loggedInCustomerId);
      show(pendingTimeoutEl, false);
      show(requestWrap, true);
      show(ownedWrap, false);
      show(empty, false);
      updateControlsVisibility();
      setStatus(status, '');
      if (loggedInCustomerId) {
        schedulePendingPoll();
      }
    }

    /* The ordinary "you have no items" state for a customer whose identity IS resolved and whose
     * /api/library call genuinely came back with zero entitlements. Extracted (2026-09-01,
     * tasks.db #815) out of loadLibrary()'s items.length===0 branch so it can also be reached as
     * the FALLBACK of a session-bridge retry (see that branch, and trySessionBridge()'s onEmpty
     * param below) — the retry's own "still empty" outcome must land here, never on showNoToken()'s
     * sign-in wall, or a genuinely-empty real customer would regress into being told to sign in. */
    function showEmptyLibrary() {
      setStatus(status, '');
      show(empty, true);
      show(ownedWrap, false);
      updateControlsVisibility();
    }

    function schedulePendingPoll() {
      if (pendingResolved || pendingTimer || pendingInFlight) return;
      if (pendingAttempt >= PENDING_DELAYS_MS.length) {
        showPendingTimeout();
        return;
      }
      pendingTimer = setTimeout(function () {
        pendingTimer = null;
        pendingAttempt++;
        pollBridgeOnce();
      }, PENDING_DELAYS_MS[pendingAttempt]);
    }

    // The automatic check has given up (rare — normally the mint lands well inside the ~4m15s
    // window). "This page updates automatically" is no longer true, so it must stop being shown.
    function showPendingTimeout() {
      show(pendingEl, false);
      show(pendingTimeoutEl, !!loggedInCustomerId);
    }

    function pollBridgeOnce() {
      // shopify:section:load re-init (theme editor) can leave a prior closure's timer running
      // against a since-removed root — bail rather than poll for a detached section.
      if (!root.isConnected) return;
      pendingInFlight = true;
      fetch(SESSION_BRIDGE_PATH, { headers: { Accept: 'application/json' }, credentials: 'include' })
        .then(function (resp) {
          if (!resp.ok) throw new Error('HTTP ' + resp.status);
          return resp.json();
        })
        .then(function (data) {
          pendingInFlight = false;
          if (data && data.token) {
            pendingResolved = true;
            storeToken(data.token);
            show(pendingEl, false);
            show(pendingTimeoutEl, false);
            show(signinEl, false);
            show(requestWrap, false);
            // tasks.db #815 audit follow-up (2026-09-01, Sonnet MEDIUM): this poll-success path
            // hands a bridge-obtained token to loadLibrary() just like the session-bridge helper's
            // own success handler does below — it must set the SAME guard, or a customer whose
            // entitlement mint just landed via this poll and who then turns out to have a
            // genuinely empty library would trip the 200-empty branch's own bridge retry (that
            // branch only checks bridgeRetried, not HOW the current token was obtained).
            bridgeRetried = true;
            loadLibrary(data.token);
            return;
          }
          schedulePendingPoll();
        })
        .catch(function (e) {
          pendingInFlight = false;
          if (window.console && console.warn) console.warn('pw-library: pending poll failed', e);
          schedulePendingPoll();
        });
    }

    /*
     * Fetch + render the library for an already-resolved token (URL/sessionStorage OR freshly
     * minted by the session bridge below) — the one code path both token sources share.
     *
     * tasks.db #816 (2026-09-01, PLAN v3 §2.4.4) -- `opts.allowPersist`. This function used to
     * unconditionally `storeToken(data.renewedToken)` on EVERY successful response (the sliding-
     * renewal fix, 2026-08-13) with no awareness of how the token it was CALLED WITH was obtained.
     * The server mints a fresh 14-day renewal on any read past 50% of a token's life, so an
     * attacker who simply waits ~8 days before mailing a hostile link got their (renewed) token
     * silently RE-PLANTED into the victim's storage the instant the victim clicked "View the
     * linked library instead" — v1's exact exploit, reopened one click later (round-2 Opus
     * MUST_FIX, the actual reason the prior design was rejected). The interstitial's "View
     * someone else's library" is the ONLY call site that ever passes `{ allowPersist: false }`.
     *
     * Round-3 MUST_FIX -- the EXACT required form: `opts.allowPersist !== false`, never a
     * truthiness check on `opts.allowPersist` directly. All three PRE-EXISTING call sites
     * (pollBridgeOnce, trySessionBridge, init's initial call) pass no second argument at all, so
     * `opts` becomes `{}` and `opts.allowPersist` is `undefined` -- falsy. A naive
     * `if (opts.allowPersist && ...)` would silently disable sliding renewal for every normal
     * customer on every existing call site, recreating the Aug-27 expiry-cliff bug this repo
     * already fixed once. Pinned by structural test (g4, exact-form regex) and behavioral test
     * (i2a, all three real call sites).
     */
    function loadLibrary(token, opts) {
      opts = opts || {};
      var allowPersist = opts.allowPersist !== false;

      // Keep the in-memory token current for the REST of this render either way (so
      // buildDownloadPlan/currentToken() see it, even for a view-only render that never touches
      // storage) — persistence itself is the separately-gated step below.
      activeToken = token;

      // Decode-first: render who's signed in from the token ITSELF, before the network call
      // below even starts — every token-arrival path (URL param, stored token, session-bridge
      // mint, pending-poll mint) funnels through here, so one call covers all of them.
      // `!allowPersist` is this render's "view-only" signal (Opus S1) — the interstitial's "View
      // someone else's library" is the only caller that ever passes allowPersist:false.
      renderIdentityBar(token, !allowPersist);

      if (!backendUrl) {
        setStatus(status, 'My Library is not configured.');
        return;
      }

      setStatus(status, 'Loading your library…');

      fetch(backendUrl + '/api/library', {
        headers: { Accept: 'application/json', Authorization: 'Bearer ' + token },
      })
        .then(function (resp) {
          if (resp.status === 401 || resp.status === 403) {
            // The server just authoritatively rejected the token whose identity we optimistically
            // showed above — hide it now rather than leave a confirmed-wrong identity displayed
            // while a retry/fallback is still in flight.
            show(identityBar, false);
            // Round-3 SHOULD_FIX (test i2b): a view-only render (allowPersist===false) must never
            // clear the VICTIM's own real credential as a side effect of curiosity-clicking a
            // hostile link whose token happens to be expired — it was never our token to clear.
            // The retry/showNoToken() fallback below still runs either way; only the storage
            // clear is skipped.
            if (allowPersist) {
              try {
                clearToken();
              } catch (e) {
                /* ignore */
              }
            }
            // Session-bridge token expiry gap (2026-07-14): the bridge mints a short-lived
            // (30-min) token, so a still-logged-in customer who reloads after it expires used to
            // hit the request-link form even though nothing about their login actually changed.
            // Retry the bridge ONCE (bridgeRetried guards against looping if it keeps failing)
            // before falling back — a real guest or a genuinely expired emailed link still ends
            // up at the same request-link form either way.
            if (loggedInCustomerId && !bridgeRetried) {
              bridgeRetried = true;
              trySessionBridge();
              return null;
            }
            // Token missing/expired/guest, or the retry above already ran — fall back to the
            // sign-in wall + request-link form.
            showNoToken();
            return null;
          }
          if (!resp.ok) throw new Error('HTTP ' + resp.status);
          return resp.json();
        })
        .then(function (data) {
          if (!data) return;
          // Sliding renewal (2026-08-13, Aug-27 cliff fix): the backend re-mints a fresh 14-day
          // token on any read past 50% of the current one's life. Re-store it so an active
          // customer's access effectively never expires. storeToken() already try/catches both
          // its localStorage and sessionStorage writes internally (storage full/blocked is
          // non-fatal there) — a second wrapper here would be unreachable dead code, so there
          // isn't one; only a defensive type check, since a malformed/non-string field must never
          // reach storage.
          if (typeof data.renewedToken === 'string' && data.renewedToken) {
            // Keep the in-memory token current regardless of allowPersist -- a view-only render's
            // zip/download flows still need a working, current token for the rest of this load.
            activeToken = data.renewedToken;
          }
          if (allowPersist && typeof data.renewedToken === 'string' && data.renewedToken) {
            storeToken(data.renewedToken);
          }
          var items = data && Array.isArray(data.items) ? data.items : [];
          if (items.length === 0) {
            // tasks.db #815 (2026-09-01, PLAN): a 200 with items:[] means the token is
            // CRYPTOGRAPHICALLY VALID but might simply belong to a different identity than
            // whoever Shopify's own session cookie says is really logged in right now (Shop Pay
            // silently authenticates as ITS OWN saved identity, distinct from any previously
            // stored library token — reproduced live, order 89753CS/89699CS). That never reached
            // the 401/403 branch above (the token isn't rejected, it's just the wrong customer's),
            // so this empty result was always taken at face value even when Shopify already knows
            // the real identity via logged_in_customer_id.
            //
            // Fix: when Liquid rendered a real (non-guest) customer.id and the bridge hasn't
            // already run this load (the SAME bridgeRetried guard the 401/403 path uses — one
            // retry per load, never a loop), try the bridge once before accepting "empty" as final.
            // A successful mint re-enters loadLibrary() with the bridge's own token; if THAT also
            // comes back empty, bridgeRetried is already true, so it falls straight through to
            // showEmptyLibrary() below — no second retry, no behavior change for a genuinely-empty
            // real customer.
            if (loggedInCustomerId && !bridgeRetried) {
              bridgeRetried = true;
              // Audit follow-up (2026-09-01, Opus H-1): deliberately do NOT clear the status line
              // here. setStatus(status, 'Loading your library…') is still showing from the top of
              // this function, and both [data-pw-empty] and [data-pw-owned-wrap] are `hidden` by
              // Liquid default — clearing status before this retry's own network round trip would
              // leave the content area completely blank for its duration (worse if the bridge call
              // hangs, since there's no timeout on any fetch in this file). Leaving the existing
              // "Loading…" message up means the screen stays painted through the retry; whichever
              // way it resolves, the next step (showEmptyLibrary() below, or loadLibrary() being
              // re-entered with a fresh token) replaces this status itself.
              trySessionBridge(showEmptyLibrary, 'empty-retry');
              return;
            }
            showEmptyLibrary();
            return;
          }
          setStatus(status, '');
          show(empty, false);
          show(ownedWrap, true);
          allItems = items;
          updateControlsVisibility();
          renderLibraryTab();
        })
        .catch(function () {
          setStatus(status, 'Something went wrong loading your library. Please refresh to try again.');
        });
    }

    /* My Library Shopify-login bridge, Phase 2 (2026-07-14, Paul: "if someone is logged in they
     * shouldn't need to send a link to themselves"). Liquid server-renders `customer.id` reliably
     * when the visitor is really logged in — that's a signal to TRY the bridge, never trusted on
     * its own (the backend's own App Proxy HMAC check is what actually verifies it). Success ->
     * store the token and load exactly as an emailed-link visit would. Any failure/no-entitlements
     * (Shop Pay identity mismatch, app not installed yet, network error) -> fall through to the
     * UNCHANGED request-link form, never an error the visitor sees.
     */
    // `onEmpty` (tasks.db #815, 2026-09-01): where to land when the bridge itself has nothing —
    // no token minted, or the fetch fails outright. Defaults to showNoToken() (the pre-existing
    // behavior for both call sites this file had before today: no stored token at all, and the
    // 401/403 retry) — a bare `trySessionBridge()` call is byte-for-byte the old behavior. Only
    // the NEW 200-empty retry above passes something else (showEmptyLibrary), because that retry
    // starts from a state that already proved this token is a real, currently-valid identity —
    // "the bridge also has nothing new" there means "genuinely empty", not "not signed in".
    //
    // `srcParam` (tasks.db #815 audit follow-up, 2026-09-01, Opus M-1): an optional attribution
    // tag appended as `?src=<value>` on the bridge request itself. Only the NEW 200-empty retry
    // passes one ('empty-retry') — the two pre-existing call sites (init()'s no-token path, and
    // loadLibrary()'s 401/403 retry) call trySessionBridge() with no second argument, so their
    // request URL is byte-for-byte unchanged. Safe to forward: verifyAppProxySignature
    // (packages/api/src/app-proxy.ts) HMACs over every forwarded query param except `signature`,
    // and Shopify signs client-supplied params too, so an added `src` still verifies server-side.
    // Without this, `library.session_bridge.*` telemetry cannot tell this retry apart from the
    // other two paths, and a genuinely-empty customer's retry would otherwise inflate
    // `no_entitlements{reason:'no-matching-entitlements'}` in a way that misreads as bridge health
    // degrading. Reading `src` back into telemetry props is a small corresponding change on the
    // `library-session.ts` API side (packages/api/src/library-session.ts in the `pagewright` repo)
    // — left as a follow-up rather than made alongside this frontend change; see PR notes.
    function trySessionBridge(onEmpty, srcParam) {
      var handleEmpty = onEmpty || showNoToken;
      var bridgeUrl = SESSION_BRIDGE_PATH + (srcParam ? '?src=' + encodeURIComponent(srcParam) : '');
      fetch(bridgeUrl, { headers: { Accept: 'application/json' }, credentials: 'include' })
        .then(function (resp) {
          if (!resp.ok) throw new Error('HTTP ' + resp.status);
          return resp.json();
        })
        .then(function (data) {
          if (!data || !data.token) {
            handleEmpty();
            return;
          }
          // 2026-08-13/14 login forensics fix: the bridge now mints a 14-day token (was 30
          // minutes), so it can legitimately be shorter-lived than an already-stored token in one
          // case — a customer holding a fresher/longer emailed or renewed token who then logs in
          // through Shopify. Never let a shorter-lived token silently downgrade a longer one; the
          // NEW token is still used in-memory for THIS load either way via loadLibrary below.
          // The actual decision (identity-aware, fails closed on an undecodable new token) lives
          // in PWAuth.shouldReplaceToken — one pure, directly-tested function — not inline here.
          try {
            if (!window.PWAuth || PWAuth.shouldReplaceToken(PWAuth.getToken(), data.token)) {
              storeToken(data.token);
            }
          } catch (e) {
            storeToken(data.token); /* unexpected error deciding — fail open to storing, matching prior behavior */
          }
          show(signinEl, false);
          show(requestWrap, false);
          // tasks.db #815 audit follow-up (2026-09-01, Sonnet MEDIUM): set the SAME guard the
          // loadLibrary()-internal retry branches set, right before handing this bridge-obtained
          // token to loadLibrary() — this is the fourth of four places a bridge-minted token can
          // reach loadLibrary(), and the only one of the four that previously left bridgeRetried
          // false. Without this, a logged-in customer with no stored token (init()'s primary,
          // no-retry call path below) whose library is genuinely empty would trip the 200-empty
          // branch's guard check and fire a second, redundant bridge call.
          bridgeRetried = true;
          loadLibrary(data.token);
        })
        .catch(function () {
          handleEmpty();
        });
    }

    initRequestForm(root, backendUrl);
    setActiveTab(initialTab());

    /*
     * tasks.db #816 (2026-09-01, PLAN v3 §2.4/§2.6) -- identity cross-check gate.
     *
     * Location-sourced token (URL fragment/query, §2.4 point 3): at mode 'off' this is
     * byte-identical to pre-#816 behavior (storeToken immediately). At observe/enforce, a
     * mismatch (session identity PRESENT and the token's owner differs) never touches storage and
     * shows the interstitial instead — active in BOTH modes, because it's non-destructive and
     * closes the credential-planting attack from the very first prod push, not only once
     * `enforce` is later armed.
     *
     * Storage-sourced token (§2.6): Layer 2 is the PRIMARY render-decision authority, not merely
     * a redundancy net — in enforce mode Layer 1 (pw-auth.js's syncIdentity) will usually have
     * already cleared a mismatched stored token before this code even runs, but this check is
     * independently sufficient on its own (proven by test b3, which stubs Layer 1 out entirely).
     */
    var info = currentTokenInfo();
    var mode = (window.PWAuth && window.PWAuth.identityCrosscheckMode) ? window.PWAuth.identityCrosscheckMode() : 'off';

    if (!info.token) {
      if (loggedInCustomerId) {
        trySessionBridge();
      } else {
        showNoToken();
      }
      return;
    }

    if (info.fromLocation) {
      if (mode === 'off') {
        storeToken(info.token);
        show(signinEl, false);
        show(requestWrap, false);
        loadLibrary(info.token);
        return;
      }
      var locCheck = crosscheckDecision(info.token, loggedInCustomerId, loggedInCustomerEmail);
      postIdentityCheckEvent(locCheck.decision, 2, mode);
      // FIX-UP (2026-09-01, Opus S3): identityCheck's own indeterminate_* fail-open rule is
      // correct for Layer 1's CLEARING decision (never clear on ambiguous data -- do NOT touch
      // identityCheck's return values or that logic, they're right as-is) but WRONG applied
      // unmodified to the location-token AUTO-PERSIST decision: `indeterminate_no_session_email`
      // (real Shopify state for SMS/phone-only accounts -- blank session email) and
      // `indeterminate_undecodable` (token doesn't decode) both used to fall through to
      // storeToken()+render below, reopening the credential-planting attack for those cohorts
      // even in enforce mode. Narrowed here, LOCATION TOKENS ONLY: gate on all three "can't
      // safely auto-persist this" decisions. All three only ever arise once identityCheck has
      // already confirmed a real session id is present (its first branch returns `match`
      // immediately whenever the session id itself is unknown), so the ~298-customer no-session
      // emailed-link/VIP cohort is untouched -- this condition is never true for them. `match`
      // and `same_person` (ids equal, or ids differ with BOTH emails confirmed known and equal)
      // are UNCHANGED: same-person dual records still never see the interstitial for a location
      // token, exactly as originally specified.
      if (
        locCheck.decision === 'mismatch' ||
        locCheck.decision === 'indeterminate_no_session_email' ||
        locCheck.decision === 'indeterminate_undecodable'
      ) {
        showIdentityInterstitial(info.token, locCheck.claims, loggedInCustomerEmail);
        return;
      }
      storeToken(info.token);
      show(signinEl, false);
      show(requestWrap, false);
      loadLibrary(info.token);
      return;
    }

    // Storage-sourced (or a token already persisted by an earlier step this same load).
    if (mode !== 'off' && loggedInCustomerId) {
      var stCheck = crosscheckDecision(info.token, loggedInCustomerId, loggedInCustomerEmail);
      postIdentityCheckEvent(stCheck.decision, 2, mode);
      if (mode === 'enforce' && stCheck.decision === 'mismatch') {
        // Token-scope clear only — Layer 2 does NOT invoke signOutCleanup(); favorites hygiene
        // (and its recovery backup) is Layer 1's job, and double-clearing here would bypass that
        // backup's write-before-clear ordering.
        clearToken();
        if (!bridgeRetried) {
          bridgeRetried = true;
          trySessionBridge();
        } else {
          showNoToken();
        }
        return;
      }
    }

    show(signinEl, false);
    show(requestWrap, false);
    loadLibrary(info.token);
  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('pw-library');
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Theme editor: re-init when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
